import { useEffect, useState } from "react";
import { Users, Plus, Trash2, Save, RefreshCw } from "lucide-react";
import api from "../../lib/api";

const ROLES = ["admin_lead", "compliance", "tech", "finance", "observer"];
const GROUPS = ["main", "onboarding", "tech", "finance"];

export default function AdminNereaRoster() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/nerea/roster");
      setEntries(data.entries || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const updateRow = (idx, patch) => {
    setEntries((e) => e.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };
  const removeRow = (idx) => setEntries((e) => e.filter((_, i) => i !== idx));
  const addRow = () =>
    setEntries((e) => [
      ...e,
      {
        roster_id: "",
        neptune_user_id: "",
        external_email: "",
        role: "compliance",
        default_groups: ["onboarding"],
        telegram_user_id: null,
        telegram_username: null,
        is_active: true,
      },
    ]);

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {
        entries: entries.map((e) => ({
          ...e,
          neptune_user_id: e.neptune_user_id || null,
          external_email: e.external_email || null,
          roster_id: e.roster_id || null,
        })),
      };
      const { data } = await api.put("/admin/nerea/roster", payload);
      // STRICT (CONTRIBUTING.md): save responses never write to user-editable
      // state. Local `entries` is the truth. We only patch in server-generated
      // metadata (new `roster_id`s for previously-unsaved rows) so the next
      // save targets them correctly. We do NOT replace the array.
      const fresh = data?.entries || [];
      setEntries((prev) => prev.map((row) => {
        if (row.roster_id) return row;
        // Match newly-saved rows by email or neptune_user_id.
        const match = fresh.find((f) =>
          (row.external_email && f.external_email === row.external_email) ||
          (row.neptune_user_id && f.neptune_user_id === row.neptune_user_id),
        );
        return match ? { ...row, roster_id: match.roster_id } : row;
      }));
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
        <Users className="h-3 w-3 text-[#00E5FF]" /> Nerea
      </div>
      <h1 className="font-display mt-3 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text">
        Roster
      </h1>
      <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed max-w-2xl">
        The Neptune team Nerea coordinates with. Roles drive the default Telegram subgroups
        each member is added to in Brief 3.4. Per-merchant overrides live on the application
        record itself.
      </p>

      <div className="mt-6 flex items-center gap-2">
        <button
          data-testid="roster-add-btn"
          onClick={addRow}
          className="inline-flex items-center gap-1.5 rounded-lg border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.05] px-3 py-1.5 text-[12.5px] text-zinc-200 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> Add row
        </button>
        <button
          data-testid="roster-save-btn"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 border border-[#00E5FF]/30 text-[#00E5FF] px-3 py-1.5 text-[12.5px] transition-colors disabled:opacity-40"
        >
          {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {saving ? "Saving…" : "Save roster"}
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="roster-table">
          <thead className="text-zinc-500 text-left text-[10.5px] uppercase tracking-[0.18em]">
            <tr>
              <th className="px-3 py-2.5">Email or user ID</th>
              <th className="px-3 py-2.5">Role</th>
              <th className="px-3 py-2.5">Default groups</th>
              <th className="px-3 py-2.5">Telegram</th>
              <th className="px-3 py-2.5">Active</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-zinc-500">Loading…</td>
              </tr>
            )}
            {!loading && entries.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-zinc-500" data-testid="roster-empty">
                  Roster is empty. Click <span className="text-zinc-300">Add row</span> to seed.
                </td>
              </tr>
            )}
            {entries.map((row, idx) => (
              <tr key={row.roster_id || `new-${idx}`} className="border-t border-white/[0.05]">
                <td className="px-3 py-2 align-top">
                  <input
                    data-testid={`roster-email-${idx}`}
                    placeholder="email or user ID"
                    value={row.external_email || row.neptune_user_id || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      const isEmail = v.includes("@");
                      updateRow(idx, {
                        external_email: isEmail ? v : null,
                        neptune_user_id: isEmail ? null : v,
                      });
                    }}
                    className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-2 py-1 text-[12.5px] text-white focus:outline-none focus:border-[#00E5FF]/40"
                  />
                </td>
                <td className="px-3 py-2 align-top">
                  <select
                    data-testid={`roster-role-${idx}`}
                    value={row.role}
                    onChange={(e) => updateRow(idx, { role: e.target.value })}
                    className="bg-white/[0.03] border border-white/[0.08] rounded px-2 py-1 text-[12.5px] text-white"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r} className="bg-[#0a0a0a]">{r}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="flex flex-wrap gap-1">
                    {GROUPS.map((g) => {
                      const checked = (row.default_groups || []).includes(g);
                      return (
                        <button
                          key={g}
                          data-testid={`roster-group-${idx}-${g}`}
                          onClick={() => {
                            const set = new Set(row.default_groups || []);
                            checked ? set.delete(g) : set.add(g);
                            updateRow(idx, { default_groups: Array.from(set) });
                          }}
                          className={`text-[11px] px-2 py-0.5 rounded border ${
                            checked
                              ? "bg-[#00E5FF]/10 border-[#00E5FF]/30 text-[#00E5FF]"
                              : "bg-white/[0.02] border-white/[0.08] text-zinc-500"
                          }`}
                        >
                          {g}
                        </button>
                      );
                    })}
                  </div>
                </td>
                <td className="px-3 py-2 align-top">
                  <input
                    data-testid={`roster-telegram-${idx}`}
                    placeholder="@username"
                    value={row.telegram_username || ""}
                    onChange={(e) =>
                      updateRow(idx, { telegram_username: e.target.value || null })
                    }
                    className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-2 py-1 text-[12.5px] text-white focus:outline-none focus:border-[#00E5FF]/40"
                  />
                </td>
                <td className="px-3 py-2 align-top">
                  <input
                    type="checkbox"
                    data-testid={`roster-active-${idx}`}
                    checked={!!row.is_active}
                    onChange={(e) => updateRow(idx, { is_active: e.target.checked })}
                    className="h-4 w-4 accent-[#00E5FF]"
                  />
                </td>
                <td className="px-3 py-2 align-top">
                  <button
                    data-testid={`roster-remove-${idx}`}
                    onClick={() => removeRow(idx)}
                    className="text-zinc-500 hover:text-rose-400 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[11.5px] text-zinc-500">
        Save replaces the entire roster atomically. Be sure to keep the row for your admin
        lead — Nerea will not work without one.
      </p>
    </div>
  );
}
